import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';
import RewardBadge from './RewardBadge';
import { cn } from '../../utils/cn';

const AchievementUnlockModal = ({ achievement, memberName, memberColor, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (achievement) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 50);
      
      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [achievement]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 300);
  };

  if (!isVisible || !achievement) return null;

  return (
    <div 
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4 transition-smooth',
        isAnimating ? 'bg-black/50' : 'bg-black/0'
      )}
      onClick={handleClose}
    >
      <div 
        className={cn(
          'bg-card rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 transform transition-all duration-500',
          isAnimating ? 'scale-100 opacity-100 translate-y-0' : 'scale-75 opacity-0 translate-y-8'
        )}
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Confetti/Celebration Effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          {[...Array(20)]?.map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse"
              style={{
                backgroundColor: i % 2 === 0 ? achievement?.color : memberColor,
                top: `${Math?.random() * 100}%`,
                left: `${Math?.random() * 100}%`,
                animationDelay: `${Math?.random() * 0.5}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg transition-smooth"
          aria-label="Close"
        >
          <Icon name="x" size={20} className="text-muted-foreground" />
        </button>

        {/* Achievement Content */}
        <div className="text-center relative z-10">
          <div className="mb-4">
            <div 
              className={cn(
                'inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 transform transition-all duration-700',
                isAnimating ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
              )}
              style={{ backgroundColor: `${achievement?.color}20` }}
            >
              <Icon 
                name={achievement?.icon} 
                size={40} 
                color={achievement?.color}
                className="animate-pulse"
              />
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            🎉 Achievement Unlocked!
          </h2>

          <div className="mb-4">
            <h3 className="text-xl font-semibold text-foreground mb-1">
              {achievement?.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {achievement?.description}
            </p>
          </div>

          {/* Member Badge */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
              style={{ backgroundColor: memberColor }}
            >
              {memberName?.charAt(0)}
            </div>
            <span className="text-base font-medium text-foreground">{memberName}</span>
          </div>

          {/* Reward Badge */}
          {achievement?.reward && (
            <div className="flex justify-center mb-6">
              <RewardBadge reward={achievement?.reward} size="lg" showLabel={true} />
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleClose}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            Awesome!
          </button>
        </div>
      </div>
    </div>
  );
};

export default AchievementUnlockModal;
